<template>
    <div class="font-sans">
        <!-- ── Filters bar ── -->
        <div class="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 mb-6">
            <!-- Search -->
            <SearchForm @search="handleSearch" placeholder="Search attendee..." class="w-full md:w-72" />

            <!-- Filters -->
            <div class="flex flex-wrap items-center gap-3 w-full md:w-auto justify-end">
                <DropDownFilter :datas="statusOptions" field="Status" label="Filter" @filter="handleCheckBoxFilter" />
            </div>
        </div>

        <!-- ── Summary chips ── -->
        <div class="flex flex-wrap gap-3 mb-5">
            <div v-for="chip in summaryChips" :key="chip.label" @click="selectChip(chip.status)"
                class="flex items-center gap-2 px-3.5 py-2 rounded-xl border cursor-pointer transition-all duration-150"
                :class="[
                    chip.status === activeChipStatus
                        ? 'ring-2 ring-primary-400 bg-primary-100 dark:bg-dark-500'
                        : 'bg-primary-50 dark:bg-dark-600',
                    'border-primary-100 dark:border-dark-500 shadow-sm'
                ]">
                <span class="w-2 h-2 rounded-full" :class="chip.dot"></span>

                <span class="text-[12px] font-semibold text-muted dark:text-muted-light">
                    {{ chip.label }}
                </span>

                <span class="text-[13px] font-bold text-muted dark:text-muted-light">
                    {{ chip.count }}
                </span>
            </div>
        </div>

        <!-- ── Loading ── -->
        <div v-if="loading" class="flex flex-col items-center justify-center py-24 gap-4">
            <div class="w-8 h-8 rounded-full border-2 border-primary-400 border-t-transparent animate-spin"></div>

            <p class="text-[11px] font-semibold tracking-widest uppercase text-gray-400">
                Loading attendees
            </p>
        </div>

        <!-- ── Empty state ── -->
        <div v-else-if="filteredAttendees.length === 0" class="flex flex-col items-center justify-center py-24 gap-3">
            <svg class="w-10 h-10 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5"
                    d="M17 20h5v-2a4 4 0 00-3-3.87M9 20H4v-2a4 4 0 013-3.87m6-1.13a4 4 0 10-4-4 4 4 0 004 4zm6-4a4 4 0 11-4-4" />
            </svg>

            <p class="text-sm font-semibold text-gray-500">
                No attendees found
            </p>

            <p class="text-xs text-gray-400">
                Try adjusting your search or filters
            </p>
        </div>

        <!-- ── Table (desktop) ── -->
        <div v-else class="overflow-x-auto rounded-xl border border-primary-100 dark:border-dark-600">
            <table class="w-full border-separate border-spacing-0">
                <thead>
                    <tr>
                        <th
                            class="text-left text-[11px] font-bold uppercase tracking-widest px-4 py-3 text-muted dark:text-muted-light bg-primary-50 dark:bg-dark-600 border-b border-primary-100 dark:border-dark-500">
                            Attendee
                        </th>

                        <th
                            class="text-left text-[11px] font-bold uppercase tracking-widest px-4 py-3 text-muted dark:text-muted-light bg-primary-50 dark:bg-dark-600 border-b border-primary-100 dark:border-dark-500">
                            Phone Number
                        </th>

                        <th
                            class="text-left text-[11px] font-bold uppercase tracking-widest px-4 py-3 text-muted dark:text-muted-light bg-primary-50 dark:bg-dark-600 border-b border-primary-100 dark:border-dark-500">
                            Qty
                        </th>

                        <th
                            class="text-left text-[11px] font-bold uppercase tracking-widest px-4 py-3 text-muted dark:text-muted-light bg-primary-50 dark:bg-dark-600 border-b border-primary-100 dark:border-dark-500">
                            Status
                        </th>

                        <th v-if="forClient"
                            class="text-left text-[11px] font-bold uppercase tracking-widest px-4 py-3 text-muted dark:text-muted-light bg-primary-50 dark:bg-dark-600 border-b border-primary-100 dark:border-dark-500">
                            Actions
                        </th>
                    </tr>
                </thead>

                <tbody>
                    <tr v-for="attendee in paginatedAttendees" :key="attendee.attendee_id"
                        class="group hover:bg-primary-50 dark:hover:bg-dark-600/50 transition-colors duration-150">
                        <!-- Attendee -->
                        <td
                            class="px-5 py-3.5 text-[13px] font-semibold text-dark-700 dark:text-white border-b border-primary-50 dark:border-dark-600 whitespace-nowrap">
                            <div class="flex items-center justify-start gap-3 min-w-0">
                                <div
                                    class="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 text-[12px] font-bold text-primary-600 bg-primary-100">
                                    {{ initials(attendee.name) }}
                                </div>

                                <div class="min-w-0">
                                    <p class="text-[13px] font-semibold dark:text-white truncate">
                                        {{ attendee.name }}
                                    </p>

                                    <p class="text-[12px] text-gray-400 truncate">
                                        {{ attendee.email }}
                                    </p>
                                </div>
                            </div>
                        </td>

                        <!-- Phone -->
                        <td
                            class="px-5 py-3.5 text-[13px] font-semibold text-dark-700 dark:text-white border-b border-primary-50 dark:border-dark-600 whitespace-nowrap">
                            <span class="text-[13px] dark:text-white">
                                {{ attendee.phone }}
                            </span>
                        </td>

                        <!-- Qty -->
                        <td
                            class="px-5 py-3.5 text-[13px] font-semibold text-dark-700 dark:text-white border-b border-primary-50 dark:border-dark-600 whitespace-nowrap">
                            <span class="text-[13px] font-semibold dark:text-white">
                                {{ attendee.ticket_quantity }}
                            </span>
                        </td>

                        <!-- Status -->
                        <td
                            class="px-5 py-3.5 text-[13px] font-semibold text-dark-700 dark:text-white border-b border-primary-50 dark:border-dark-600 whitespace-nowrap">
                            <span
                                class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold"
                                :class="statusStyle(attendee.ticket_status)">
                                <span class="w-1.5 h-1.5 rounded-full"
                                    :class="statusDot(attendee.ticket_status)"></span>

                                {{ attendee.ticket_status }}
                            </span>
                        </td>

                        <!-- Actions -->
                        <td v-if="forClient"
                            class="px-5 py-3.5 text-[13px] font-semibold text-dark-700 dark:text-white border-b border-primary-50 dark:border-dark-600 whitespace-nowrap">
                            <div class="flex items-center justify-start gap-2">
                                <!-- Reserved -->
                                <template v-if="
                                    attendee.ticket_status === 'reserved'
                                ">
                                    <button @click="
                                        updateStatus(attendee, 'accept')
                                        "
                                        class="px-3 py-1.5 rounded-lg text-[11px] font-bold bg-primary-400 text-white hover:bg-primary-600 transition-all duration-150">
                                        Approve
                                    </button>

                                    <button @click="
                                        updateStatus(attendee, 'cancel')
                                        "
                                        class="px-3 py-1.5 rounded-lg text-[11px] font-bold bg-red-100 text-red-500 hover:bg-red-200 transition-all duration-150">
                                        Decline
                                    </button>
                                </template>

                                <!-- Confirmed -->
                                <template v-else-if="
                                    attendee.ticket_status === 'confirmed'
                                ">
                                    <button @click="
                                        updateStatus(attendee, 'cancel')
                                        "
                                        class="px-3 py-1.5 rounded-lg text-[11px] font-bold bg-red-100 text-red-500 hover:bg-red-200 transition-all duration-150">
                                        Decline
                                    </button>
                                </template>

                                <!-- Cancelled -->
                                <template v-else-if="
                                    attendee.ticket_status === 'cancelled'
                                ">
                                    <button @click="
                                        updateStatus(attendee, 'accept')
                                        "
                                        class="px-3 py-1.5 rounded-lg text-[11px] font-bold bg-primary-400 text-white hover:bg-primary-600 transition-all duration-150">
                                        Approve
                                    </button>
                                </template>
                            </div>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>

        <!-- ── Pagination ── -->
        <div v-if="
            !loading &&
            filteredAttendees.length > pageSize
        " class="flex items-center justify-between mt-5">
            <p class="text-[12px] text-gray-400">
                Showing
                {{ (currentPage - 1) * pageSize + 1 }}–{{
                    Math.min(
                        currentPage * pageSize,
                        filteredAttendees.length
                    )
                }}
                of {{ filteredAttendees.length }}
            </p>

            <div class="flex items-center gap-2">
                <button @click="currentPage--" :disabled="currentPage === 1"
                    class="w-8 h-8 rounded-lg border border-gray-100 flex items-center justify-center text-gray-400 disabled:opacity-30 hover:bg-gray-50 transition-all duration-150">
                    ‹
                </button>

                <span class="text-[12px] font-semibold text-gray-600">
                    {{ currentPage }} / {{ totalPages }}
                </span>

                <button @click="currentPage++" :disabled="currentPage === totalPages"
                    class="w-8 h-8 rounded-lg border border-gray-100 flex items-center justify-center text-gray-400 disabled:opacity-30 hover:bg-gray-50 transition-all duration-150">
                    ›
                </button>
            </div>
        </div>
    </div>
</template>

<script>
import { apiDataService } from "@/services/apiDataService";
import SearchForm from "@/components/SearchForm.vue";
import { showToast } from "@/constants/toast";

export default {
    name: "AttendeeList",

    components: {
        SearchForm,
    },

    data() {
        return {
            attendees: [],
            loading: true,

            searchQuery: "",

            selectedCheckBoxFilters: [],

            // Status selected from the summary chips
            activeChipStatus: null,

            currentPage: 1,
            pageSize: 10,

            statusOptions: [
                { Status: "reserved" },
                { Status: "confirmed" },
                { Status: "cancelled" },
            ],
        };
    },

    computed: {
        userInfo() {
            return this.$store.state.auth.user || {};
        },

        forAdmin() {
            const type = Number(this.userInfo.user_type);

            return type === 0;
        },

        forClient() {
            const type = Number(this.userInfo.user_type);

            return type !== 1 && type === 2;
        },

        filteredAttendees() {
            let list = [...this.attendees];

            // Search filter
            if (this.searchQuery) {
                const q = this.searchQuery.toLowerCase();

                list = list.filter((a) => {
                    const name = a.name?.toLowerCase() || "";
                    const email = a.email?.toLowerCase() || "";

                    return (
                        name.includes(q) ||
                        email.includes(q)
                    );
                });
            }

            // Dropdown status filter
            if (this.selectedCheckBoxFilters.length > 0) {
                list = list.filter((a) =>
                    this.selectedCheckBoxFilters.includes(
                        a.ticket_status
                    )
                );
            }

            // Summary chip filter
            if (this.activeChipStatus) {
                list = list.filter(
                    (a) =>
                        a.ticket_status ===
                        this.activeChipStatus
                );
            }

            return list;
        },

        paginatedAttendees() {
            const start =
                (this.currentPage - 1) * this.pageSize;

            return this.filteredAttendees.slice(
                start,
                start + this.pageSize
            );
        },

        totalPages() {
            return Math.max(
                1,
                Math.ceil(
                    this.filteredAttendees.length /
                    this.pageSize
                )
            );
        },

        summaryChips() {
            const count = (status) =>
                this.attendees.filter(
                    (a) => a.ticket_status === status
                ).length;

            return [
                {
                    label: "Total",
                    count: this.attendees.length,
                    dot: "bg-gray-300",
                    status: null,
                },
                {
                    label: "Reserved",
                    count: count("reserved"),
                    dot: "bg-amber-400",
                    status: "reserved",
                },
                {
                    label: "Confirmed",
                    count: count("confirmed"),
                    dot: "bg-primary-400",
                    status: "confirmed",
                },
                {
                    label: "Cancelled",
                    count: count("cancelled"),
                    dot: "bg-red-400",
                    status: "cancelled",
                },
            ];
        },
    },

    methods: {
        async fetchAttendees() {
            this.loading = true;

            try {
                const eventId = this.$route.params.id;

                const response =
                    await apiDataService.getEventAttendees(
                        eventId
                    );

                this.attendees = response?.data || [];

                // Make sure pagination is valid
                this.currentPage = 1;
            } catch (error) {
                showToast(
                    "error",
                    "Failed to load attendees"
                );

                this.attendees = [];
            } finally {
                this.loading = false;
            }
        },

        async updateStatus(attendee, action) {
            const previous = attendee.ticket_status;

            // Optimistic update
            attendee.ticket_status =
                action === "accept"
                    ? "confirmed"
                    : "cancelled";

            try {
                const response =
                    await apiDataService.updateAttendeeStatus(
                        {
                            attendee_id:
                                attendee.attendee_id,
                            ticket_id:
                                attendee.ticket_id,
                            event_id:
                                attendee.event_id,
                        },
                        action
                    );

                showToast(
                    "success",
                    response?.data?.data?.message ||
                    "Attendee status updated successfully"
                );

                await this.fetchAttendees();
            } catch (error) {
                // Restore previous status
                attendee.ticket_status = previous;

                const errorMessage =
                    error?.response?.data?.data?.error
                        ?.message ||
                    error?.response?.data?.message ||
                    "Failed to update attendee status";

                showToast("error", errorMessage);
            }
        },

        viewAttendee(attendee) {
            this.$router.push(
                `/admin/attendee/${attendee.id}`
            );
        },

        initials(name) {
            if (!name) return "";

            return name
                .split(" ")
                .map((n) => n[0])
                .slice(0, 2)
                .join("")
                .toUpperCase();
        },

        formatDate(dateStr) {
            if (!dateStr) return "";

            return new Date(dateStr).toLocaleDateString(
                "en-GB",
                {
                    day: "numeric",
                    month: "short",
                    year: "numeric",
                }
            );
        },

        statusStyle(status) {
            return {
                reserved:
                    "bg-amber-50 text-amber-600",
                confirmed:
                    "bg-primary-50 text-primary-600",
                cancelled:
                    "bg-red-50 text-red-500",
            }[status] ||
                "bg-gray-100 text-gray-500";
        },

        statusDot(status) {
            return {
                reserved: "bg-amber-400",
                confirmed: "bg-primary-400",
                cancelled: "bg-red-400",
            }[status] || "bg-gray-300";
        },

        handleSearch(query) {
            this.searchQuery = query;

            this.currentPage = 1;
        },

        handleCheckBoxFilter(values) {
            this.selectedCheckBoxFilters = values;

            // Reset chip when using dropdown filter
            this.activeChipStatus = null;

            this.currentPage = 1;
        },

        selectChip(status) {
            // Total chip
            if (!status) {
                this.activeChipStatus = null;
            }
            // Click the same status again -> clear filter
            else if (this.activeChipStatus === status) {
                this.activeChipStatus = null;
            }
            // Select a new status
            else {
                this.activeChipStatus = status;
            }

            // Reset dropdown filter when using summary chip
            this.selectedCheckBoxFilters = [];

            // Reset pagination
            this.currentPage = 1;
        },
    },

    created() {
        this.fetchAttendees();
    },
};
</script>